import React from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
} from "@mui/material";
import { Delete as DeleteIcon, Edit as EditIcon, UploadFile as UploadFileIcon } from "@mui/icons-material";
import PageContainer from "../layout/PageContainer";
import usePartners from "../../hooks/usePartners";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const blank = {
    title: "",
    link_url: "",
    sort_order: 0,
    is_active: true,
};

function resolveUrl(pathOrUrl) {
    if (!pathOrUrl) return "";
    const value = String(pathOrUrl).trim();
    if (/^https?:\/\//i.test(value) || value.startsWith("blob:")) return value;
    return `${API_BASE}${value.startsWith("/") ? value : `/${value}`}`;
}

export default function PartnersPage() {
    const { partners, loading, error, fetchPartners, createPartner, updatePartner, deletePartner } = usePartners();
    const [search, setSearch] = React.useState("");
    const [open, setOpen] = React.useState(false);
    const [form, setForm] = React.useState(blank);
    const [editing, setEditing] = React.useState(null);
    const [imageFile, setImageFile] = React.useState(null);
    const [imagePreview, setImagePreview] = React.useState("");
    const [submitError, setSubmitError] = React.useState("");
    const [submitting, setSubmitting] = React.useState(false);
    const [deleteTarget, setDeleteTarget] = React.useState(null);
    const [deleteError, setDeleteError] = React.useState("");
    const [deleting, setDeleting] = React.useState(false);

    React.useEffect(() => {
        return () => {
            if (imagePreview?.startsWith("blob:")) URL.revokeObjectURL(imagePreview);
        };
    }, [imagePreview]);

    const rows = React.useMemo(() => {
        const q = search.trim().toLowerCase();
        const list = [...(partners || [])].sort((a, b) => (
            Number(a.sort_order || 0) - Number(b.sort_order || 0) ||
            String(a.title || "").localeCompare(String(b.title || ""), "de")
        ));
        if (!q) return list;
        return list.filter((partner) => (
            String(partner.title || "").toLowerCase().includes(q) ||
            String(partner.link_url || "").toLowerCase().includes(q)
        ));
    }, [partners, search]);

    const setPickedFile = (file) => {
        if (imagePreview?.startsWith("blob:")) URL.revokeObjectURL(imagePreview);
        setImageFile(file || null);
        setImagePreview(file ? URL.createObjectURL(file) : "");
    };

    const openCreate = () => {
        setEditing(null);
        setForm(blank);
        setPickedFile(null);
        setSubmitError("");
        setOpen(true);
    };

    const openEdit = (partner) => {
        setEditing(partner);
        setForm({
            ...blank,
            ...partner,
            sort_order: partner.sort_order ?? 0,
            is_active: partner.is_active !== undefined ? !!partner.is_active : true,
        });
        setPickedFile(null);
        setImagePreview(resolveUrl(partner.image_url));
        setSubmitError("");
        setOpen(true);
    };

    const handleSave = async () => {
        const title = (form.title || "").trim();
        if (!title) {
            setSubmitError("Titel ist Pflichtfeld.");
            return;
        }

        const payload = {
            title,
            link_url: (form.link_url || "").trim() || null,
            sort_order: Number(form.sort_order || 0),
            is_active: !!form.is_active,
        };

        setSubmitting(true);
        setSubmitError("");
        try {
            if (editing) await updatePartner(editing.id, payload, imageFile);
            else await createPartner(payload, imageFile);
            setOpen(false);
        } catch (e) {
            setSubmitError(e?.response?.data?.error || e.message || "Speichern fehlgeschlagen.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PageContainer
            title="Partner"
            actions={
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="outlined" onClick={fetchPartners} disabled={loading}>
                        Aktualisieren
                    </Button>
                    <Button variant="contained" onClick={openCreate}>
                        Neuer Partner
                    </Button>
                </Box>
            }
        >
            {error && <Alert severity="error">{error}</Alert>}

            <Box sx={{ mb: 2 }}>
                <TextField
                    fullWidth
                    size="small"
                    label="Partner suchen"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: "70vh", overflow: "auto" }}>
                {loading ? (
                    <Box sx={{ py: 6, textAlign: "center" }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Bild</TableCell>
                                <TableCell>Titel</TableCell>
                                <TableCell>Link</TableCell>
                                <TableCell>Aktiv</TableCell>
                                <TableCell>Sortierung</TableCell>
                                <TableCell align="right">Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((partner) => (
                                <TableRow key={partner.id} hover>
                                    <TableCell>
                                        {partner.image_url ? (
                                            <Box component="img" src={resolveUrl(partner.image_url)} alt={partner.title} sx={{ maxWidth: 120, maxHeight: 54, objectFit: "contain" }} />
                                        ) : (
                                            <Alert severity="info" icon={false} sx={{ py: 0 }}>Kein Bild</Alert>
                                        )}
                                    </TableCell>
                                    <TableCell>{partner.title}</TableCell>
                                    <TableCell>
                                        {partner.link_url ? (
                                            <Link href={partner.link_url} target="_blank" rel="noopener noreferrer">
                                                {partner.link_url}
                                            </Link>
                                        ) : "-"}
                                    </TableCell>
                                    <TableCell>{partner.is_active ? "Ja" : "Nein"}</TableCell>
                                    <TableCell>{partner.sort_order ?? 0}</TableCell>
                                    <TableCell align="right">
                                        <IconButton onClick={() => openEdit(partner)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton color="error" onClick={() => { setDeleteTarget(partner); setDeleteError(""); }}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            <Dialog open={open} onClose={() => !submitting && setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editing ? "Partner bearbeiten" : "Neuen Partner anlegen"}</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: "grid", gap: 2 }}>
                        <TextField label="Titel*" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
                        <TextField label="Link" placeholder="https://" value={form.link_url || ""} onChange={(e) => setForm((f) => ({ ...f, link_url: e.target.value }))} />
                        <TextField label="Sortierung" type="number" value={form.sort_order ?? 0} onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))} />
                        <FormControlLabel
                            control={<Checkbox checked={!!form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />}
                            label="Aktiv"
                        />

                        <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
                            Bild hochladen / ersetzen
                            <input
                                hidden
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                    setPickedFile(e.target.files?.[0] || null);
                                    e.target.value = "";
                                }}
                            />
                        </Button>

                        {imagePreview && (
                            <Paper variant="outlined" sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 130 }}>
                                <Box component="img" src={imagePreview} alt="Vorschau" sx={{ maxWidth: "100%", maxHeight: 120, objectFit: "contain" }} />
                            </Paper>
                        )}
                    </Box>
                    {submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} disabled={submitting}>Abbrechen</Button>
                    <Button variant="contained" onClick={handleSave} disabled={submitting}>
                        {submitting ? "Speichern..." : "Speichern"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} fullWidth maxWidth="xs">
                <DialogTitle>Partner löschen</DialogTitle>
                <DialogContent dividers>
                    {deleteError ? (
                        <Alert severity="error">{deleteError}</Alert>
                    ) : (
                        <Alert severity="warning">
                            Willst du <b>{deleteTarget?.title}</b> wirklich löschen?
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Abbrechen</Button>
                    <Button
                        color="error"
                        variant="contained"
                        disabled={deleting}
                        onClick={async () => {
                            if (!deleteTarget) return;
                            setDeleting(true);
                            setDeleteError("");
                            try {
                                await deletePartner(deleteTarget.id);
                                setDeleteTarget(null);
                            } catch (e) {
                                setDeleteError(e?.response?.data?.error || e.message || "Löschen fehlgeschlagen.");
                            } finally {
                                setDeleting(false);
                            }
                        }}
                    >
                        {deleting ? "Löschen..." : "Löschen"}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
}
